import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useState } from "react";
import styled from "styled-components";
import { ToastContext } from "context/ToastContext";
import {
  useHstImportMutation,
  IHSTImportResult,
  IHSTImportRowResult,
} from "store/queries/integration";
import { sizer } from "layout/styles/styled/sizer";

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(4)};
  min-width: 500px;
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(2)};
`;

const FileLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
`;

const FileInfo = styled.span`
  font-size: 12px;
  color: var(--text-color-secondary);
`;

const SelectedFileDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${sizer(2)};
  background-color: var(--surface-ground);
  border-radius: 4px;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(3)};
`;

const ResultsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${sizer(2)};
`;

type SummaryCardVariant = "success" | "error" | "warning" | "info" | "neutral";

const SummaryCard = styled.div<{ variant?: SummaryCardVariant }>`
  padding: ${sizer(2)};
  border-radius: 4px;
  text-align: center;
  background-color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "var(--green-50)";
      case "error":
        return "var(--red-50)";
      case "warning":
        return "var(--yellow-50)";
      default:
        return "var(--surface-ground)";
    }
  }};

  .count {
    font-size: 24px;
    font-weight: bold;
    color: ${({ variant }) => {
      switch (variant) {
        case "success":
          return "var(--green-600)";
        case "error":
          return "var(--red-600)";
        case "warning":
          return "var(--yellow-600)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .label {
    font-size: 12px;
    color: var(--text-color-secondary);
  }
`;

const ErrorList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
`;

const ErrorItem = styled.div`
  padding: ${sizer(2)};
  border-bottom: 1px solid var(--surface-border);
  font-size: 12px;

  &:last-child {
    border-bottom: none;
  }

  .patient-name {
    font-weight: 600;
  }

  .error-details {
    color: var(--red-600);
    margin-top: 4px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${sizer(8)};
  gap: ${sizer(2)};
`;

interface IHSTImportDialogProps {
  visible: boolean;
  onHide: () => void;
  accountId: string;
  onSuccess?: () => void;
}

export function HSTImportDialog({
  visible,
  onHide,
  accountId,
  onSuccess,
}: IHSTImportDialogProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [scheduledAppointmentsFile, setScheduledAppointmentsFile] =
    useState<File | null>(null);
  const [insuranceInfoFile, setInsuranceInfoFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<IHSTImportResult | null>(
    null
  );

  const [hstImport, { isLoading }] = useHstImportMutation();

  const handleScheduledAppointmentsSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setScheduledAppointmentsFile(e.files[0]);
    }
  };

  const handleInsuranceInfoSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setInsuranceInfoFile(e.files[0]);
    }
  };

  const handleImport = async () => {
    if (!scheduledAppointmentsFile || !insuranceInfoFile) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select both CSV files before importing",
      });
      return;
    }

    try {
      const result = await hstImport({
        accountId,
        scheduledAppointments: scheduledAppointmentsFile,
        insuranceInfo: insuranceInfoFile,
      }).unwrap();

      setImportResult(result.data);

      if (result.data.successCount > 0) {
        toastElement?.show({
          severity: "success",
          summary: "Import Complete",
          detail: `Successfully created ${result.data.successCount} orders`,
        });
        onSuccess?.();
      }

      if (result.data.errorCount > 0) {
        toastElement?.show({
          severity: "warn",
          summary: "Import Completed with Errors",
          detail: `${result.data.errorCount} rows had errors`,
        });
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Import Failed",
        detail: error.data?.message || "An error occurred during import",
      });
    }
  };

  const handleClose = () => {
    setScheduledAppointmentsFile(null);
    setInsuranceInfoFile(null);
    setImportResult(null);
    onHide();
  };

  const canImport =
    scheduledAppointmentsFile && insuranceInfoFile && !isLoading;

  // Get rows with errors (excluding duplicates)
  const errorRows =
    importResult?.results.filter((r) => !r.success && !r.skipped && r.errors) ??
    [];

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={importResult ? "Close" : "Cancel"}
        onClick={handleClose}
        className="p-button-text"
      />
      {!importResult && (
        <Button
          label="Import"
          onClick={handleImport}
          disabled={!canImport}
          loading={isLoading}
        />
      )}
    </div>
  );

  const renderDialogContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span>Importing orders... This may take a few moments.</span>
        </LoadingContainer>
      );
    }

    if (importResult) {
      return (
        <ResultsContainer>
          <ResultsSummary>
            <SummaryCard variant="info">
              <div className="count">{importResult.totalRows}</div>
              <div className="label">Total Rows</div>
            </SummaryCard>
            <SummaryCard variant="success">
              <div className="count">{importResult.successCount}</div>
              <div className="label">Created</div>
            </SummaryCard>
            <SummaryCard variant="neutral">
              <div className="count">{importResult.skippedPayerCount}</div>
              <div className="label">Skipped</div>
            </SummaryCard>
            <SummaryCard variant="warning">
              <div className="count">{importResult.duplicateCount}</div>
              <div className="label">Duplicates</div>
            </SummaryCard>
            <SummaryCard variant="error">
              <div className="count">{importResult.errorCount}</div>
              <div className="label">Errors</div>
            </SummaryCard>
          </ResultsSummary>

          {importResult.noInsuranceMatchCount > 0 && (
            <FileInfo>
              Note: {importResult.noInsuranceMatchCount} rows did not have
              matching insurance info.
            </FileInfo>
          )}

          {errorRows.length > 0 && (
            <>
              <FileLabel>Errors ({errorRows.length})</FileLabel>
              <ErrorList>
                {errorRows.slice(0, 50).map((row, index) => (
                  <ErrorItem key={index}>
                    <div className="patient-name">
                      {row.patientName} ({row.patIdVisit})
                    </div>
                    <div className="error-details">
                      {Object.entries(row.errors || {}).map(([key, value]) => (
                        <div key={key}>
                          {key}: {value}
                        </div>
                      ))}
                    </div>
                  </ErrorItem>
                ))}
                {errorRows.length > 50 && (
                  <ErrorItem>
                    ... and {errorRows.length - 50} more errors
                  </ErrorItem>
                )}
              </ErrorList>
            </>
          )}
        </ResultsContainer>
      );
    }

    return (
      <DialogContent>
        <FileSection>
          <FileLabel>Scheduled Appointments CSV</FileLabel>
          <FileInfo>
            Upload the DE5501 - Scheduled Appointments report from HST
          </FileInfo>
          {scheduledAppointmentsFile ? (
            <SelectedFileDisplay>
              <span>{scheduledAppointmentsFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setScheduledAppointmentsFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleScheduledAppointmentsSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>

        <FileSection>
          <FileLabel>Patient Insurance Info CSV</FileLabel>
          <FileInfo>
            Upload the SC1010 - Patient Insurance Info report from HST
          </FileInfo>
          {insuranceInfoFile ? (
            <SelectedFileDisplay>
              <span>{insuranceInfoFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setInsuranceInfoFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleInsuranceInfoSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>
      </DialogContent>
    );
  };

  return (
    <Dialog
      header="Import Orders from HST"
      visible={visible}
      onHide={handleClose}
      footer={footerContent}
    >
      {renderDialogContent()}
    </Dialog>
  );
}
